import { Coding, DocumentReference } from "@medplum/fhirtypes";

import { QuiltedHealthCodeSystems } from "@/fhir/enums/quilted-health";
import { PAGE_PARAM } from "@/utils/search-params";

export enum DotPhraseScope {
  Shared = "shared",
  Personal = "personal",
}

/**
 * `DocumentReference.type`. Set this on every phrase, without exception.
 *
 * The access policies keep phrases out of the practice's broad document grant by excluding this
 * code (`type:not=`). R4 makes `type` optional, so a phrase saved without it falls back into that
 * broad grant. It then becomes readable across the whole practice, including other practitioners'
 * personal phrases.
 */
export const DOT_PHRASE_TYPE_CODING = {
  system: QuiltedHealthCodeSystems.DotPhraseType,
  code: "dotphrase",
  display: "Dotphrase",
} as const satisfies Coding;

/**
 * `DocumentReference.category`, and the `List.code` of the collection that groups it.
 *
 * The `personal` scope uses the code `dotphrase-private`. The access-policy criteria write these
 * codes out as literals, so changing either code means reseeding the policies.
 */
export const DOT_PHRASE_SCOPE_CODINGS = {
  [DotPhraseScope.Shared]: {
    system: QuiltedHealthCodeSystems.DotPhraseScope,
    code: "dotphrase-shared",
    display: "Shared Dotphrase",
  },
  [DotPhraseScope.Personal]: {
    system: QuiltedHealthCodeSystems.DotPhraseScope,
    code: "dotphrase-private",
    display: "Private Dotphrase",
  },
} as const satisfies Record<DotPhraseScope, Coding>;

/** An active phrase. R4 allows only three `status` codes, so archiving reuses `superseded`. */
export const DOT_PHRASE_ACTIVE_STATUS = "current";

/** An archived phrase: gone from autocomplete and from the default list, still restorable. */
export const DOT_PHRASE_ARCHIVED_STATUS = "superseded";

/**
 * What a phrase is flipped to when a step after its create fails.
 *
 * The record cannot be deleted — no policy grants that, deliberately — and leaving it `current` would
 * leave an unreachable phrase holding its trigger. `entered-in-error` says exactly what happened,
 * keeps the attempt in `_history` for support, and is excluded from every list read, which is the
 * reversible shape this codebase prefers over a hard delete.
 */
export const DOT_PHRASE_ENTERED_IN_ERROR = "entered-in-error";

/**
 * The statuses that hold a trigger, as one token param — a comma is FHIR search's OR. An archived
 * phrase keeps its own trigger, so archiving never frees one and a restore can never collide;
 * deleting the phrase is what makes it available again.
 */
export const DOT_PHRASE_TRIGGER_RESERVED_STATUSES = `${DOT_PHRASE_ACTIVE_STATUS},${DOT_PHRASE_ARCHIVED_STATUS}`;

/** `List.title` for a scope's collection. Separate from `DOT_PHRASE_SCOPE_LABELS` so renaming a UI
 * tab does not rename stored resources. */
export const DOT_PHRASE_LIST_TITLES: Record<DotPhraseScope, string> = {
  [DotPhraseScope.Shared]: "Shared Dotphrases",
  [DotPhraseScope.Personal]: "My Dotphrases",
};

export const DOT_PHRASE_SCOPE_LABELS: Record<DotPhraseScope, string> = {
  [DotPhraseScope.Shared]: "Shared Dotphrases",
  [DotPhraseScope.Personal]: "My Dotphrases",
};

export const DOT_PHRASE_SEARCH_PLACEHOLDERS: Record<DotPhraseScope, string> = {
  [DotPhraseScope.Shared]: "Search shared Dotphrases",
  [DotPhraseScope.Personal]: "Search my Dotphrases",
};

export enum DotPhraseStatus {
  All = "all",
  Archived = "archived",
}

/** What the table says when it has no rows. A search that matched nothing is a different result
 * from a scope with nothing in it, and a new phrase would not match the term either. */
export const DOT_PHRASE_EMPTY_TABLE_TEXT = {
  scope: "No dotphrases have been added.",
  search: "No dotphrases match this search.",
} as const;

export const DOT_PHRASE_STATUS_OPTIONS: { value: DotPhraseStatus; label: string }[] = [
  { value: DotPhraseStatus.All, label: "All" },
  { value: DotPhraseStatus.Archived, label: "Archived" },
];

/**
 * What each filter value asks the server for. The filter offers only All and Archived, so `All`
 * means "not archived" and maps to the active status alone. If the design later means it to include
 * archived rows, this map is the one place that changes.
 */
export const DOT_PHRASE_FILTER_STATUSES: Record<DotPhraseStatus, DocumentReference["status"]> = {
  [DotPhraseStatus.All]: DOT_PHRASE_ACTIVE_STATUS,
  [DotPhraseStatus.Archived]: DOT_PHRASE_ARCHIVED_STATUS,
};

export const DOT_PHRASE_TAB_PARAM = "tab" as const;
export const DOT_PHRASE_STATUS_PARAM = "status" as const;
export const DOT_PHRASE_PAGE_PARAM = PAGE_PARAM;
export const DOT_PHRASE_SEARCH_PARAM = "q" as const;

/** Long enough that typing a term does not send a read per character. */
export const DOT_PHRASE_SEARCH_DEBOUNCE_MS = 300;

export const DOT_PHRASE_PAGE_SIZE = 10;

/**
 * Separates a phrase's title from its body inside `DocumentReference.description`.
 *
 * A `description:contains` query reads across the marker, so it may carry no letters, no digits,
 * and none of `%`, `_`, `|` or `#`. Letters would become terms a provider could match on. `%` and
 * `_` are SQL `LIKE` wildcards, `|` separates a FHIR token, and `#` starts a URL fragment. `-` and
 * `=` mean nothing in all four.
 */
export const DOT_PHRASE_SEPARATOR_MARKER = "--=--";

/**
 * Everything before the first occurrence is the title. Everything after it is the body's
 * plain-text projection. Written on every save, even for an empty body, so a missing separator
 * means the record is malformed.
 *
 * Never split on it by hand. `parseDotPhraseDescription` is the only reader that may touch it, and
 * `buildDotPhraseDescription` is its single matching writer.
 */
export const DOT_PHRASE_TITLE_BODY_SEPARATOR = `\n${DOT_PHRASE_SEPARATOR_MARKER}\n`;

export const DOT_PHRASE_SEPARATOR_MARKER_ERROR = `A dotphrase cannot contain "${DOT_PHRASE_SEPARATOR_MARKER}".`;

/** The body's authoritative representation. `description` only carries a projection of it. */
export const DOT_PHRASE_BODY_CONTENT_TYPE = "text/html";

export const DOT_PHRASE_PREVIEW_MAX_LENGTH = 160;

export const DOT_PHRASE_SUGGESTION_LIMIT = 20;

export const DOT_PHRASE_SUGGESTION_SECTION_LABEL = "Dotphrases";

interface DotPhraseScopeBadge {
  label: string;
  bg: string;
  c: string;
}

export const DOT_PHRASE_SCOPE_BADGES: Record<DotPhraseScope, DotPhraseScopeBadge> = {
  [DotPhraseScope.Shared]: { label: "Shared", bg: "gray.1", c: "gray.6" },
  [DotPhraseScope.Personal]: { label: "Personal", bg: "burgundy.1", c: "burgundy.9" },
};

export const DOT_PHRASE_SUGGESTION_MAX_HEIGHT = 280;

export const DOT_PHRASE_FIELD_COUNT_HINT = (fieldCount: number): string =>
  `${fieldCount} field${fieldCount === 1 ? "" : "s"} to fill`;

export const DOT_PHRASE_FIELD_NAVIGATION_HINT = "· Tab or Enter to move through them";

export const DOT_PHRASE_TRIGGER_PREFIX = ".";

/** Everything after the leading dot. Narrow on purpose: a trigger is typed mid-note, so spaces and
 * punctuation would blur where it ends. */
export const DOT_PHRASE_TRIGGER_PATTERN = /^\.[A-Za-z0-9_-]+$/;

export enum DotPhraseArchiveAction {
  Archive = "archive",
  Restore = "restore",
}

export const DOT_PHRASE_ACTION_LABELS = {
  edit: "Edit Dotphrase",
  copy: "Copy to My Dotphrases",
  delete: "Delete Dotphrase",
  [DotPhraseArchiveAction.Archive]: "Archive Dotphrase",
  [DotPhraseArchiveAction.Restore]: "Unarchive Dotphrase",
} as const;

interface DotPhraseArchiveToggleCopy {
  successTitle: string;
  successMessage: (trigger: string) => string;
  errorTitle: string;
}

/** Only the trigger is echoed back: a phrase body is clinician-authored template text and does not
 * belong in a notification. */
export const DOT_PHRASE_ARCHIVE_TOGGLE_COPY: Record<
  DotPhraseArchiveAction,
  DotPhraseArchiveToggleCopy
> = {
  [DotPhraseArchiveAction.Archive]: {
    successTitle: "Dotphrase archived",
    successMessage: (trigger) => `${trigger} stays reserved and will not appear when you type it.`,
    errorTitle: "Failed to archive dotphrase",
  },
  [DotPhraseArchiveAction.Restore]: {
    successTitle: "Dotphrase restored",
    successMessage: (trigger) => `${trigger} is ready to use again.`,
    errorTitle: "Failed to restore dotphrase",
  },
};

/**
 * The confirmation names no phrase, which is what the design asks for. It opens from one row's
 * menu, so the row is already the context. Only the toast echoes the trigger, and never the body.
 */
export const DOT_PHRASE_DELETE_COPY = {
  confirmTitle: "Delete this dotphrase?",
  confirmDescription: "This dotphrase will be permanently deleted. This can’t be undone.",
  confirmButtonText: "Yes, Delete Dotphrase",
  successTitle: "Dotphrase deleted",
  successMessage: (trigger: string) => `${trigger} is free to use for a new dotphrase.`,
  errorTitle: "Failed to delete dotphrase",
} as const;

export const DOT_PHRASE_SUCCESS_TOAST_AUTO_CLOSE = 3000;

export const DOT_PHRASE_SAVE_NOTIFICATIONS = {
  add: {
    title: "Dotphrase saved",
    message: (trigger: string) => `${trigger} is ready to use.`,
  },
  edit: {
    title: "Dotphrase updated",
    message: (trigger: string) => `${trigger} is ready to use.`,
  },
  copy: {
    title: "Dotphrase copied",
    message: (trigger: string) => `${trigger} is now in My Dotphrases.`,
  },
} as const;

export const DOT_PHRASE_ARCHIVED_EDIT_NOTIFICATION = {
  title: "Dotphrase updated",
  message: (trigger: string) => `${trigger} is still archived. Unarchive it to use it again.`,
} as const;

export const DOT_PHRASE_DRAWER_TITLES = {
  add: "Add Dotphrase",
  edit: "Edit Dotphrase",
} as const;

export const DOT_PHRASE_TITLE_PLACEHOLDER = "e.g. Cervical exam";

/** Semibold, a step heavier than Mantine's default label. Matches the other settings drawers. */
export const DOT_PHRASE_LABEL_PROPS = { fw: 600 } as const;

export const DOT_PHRASE_BODY_PLACEHOLDER =
  "e.g. Cervical exam: [4] cm dilated, [80]% effaced, [-1] station.";

/** Uniqueness is per list, so the error names which list collided: a personal `.labor` and a
 * shared `.labor` may coexist. The archived hint is there because the phrase holding the trigger
 * may be archived, and so absent from the list the reader is looking at. */
export const DOT_PHRASE_DUPLICATE_TRIGGER_ERRORS: Record<
  DotPhraseScope,
  (trigger: string) => string
> = {
  [DotPhraseScope.Shared]: (trigger) =>
    `This practice already has a shared dotphrase using ${trigger}. It may be archived — check the Archived filter.`,
  [DotPhraseScope.Personal]: (trigger) =>
    `You already have a personal dotphrase using ${trigger}. It may be archived — check the Archived filter.`,
};
